import { useMemo } from 'react';

import { ALL_PRODUCTS_STRING } from '../../../common/constants/analytics';
import { getCommonQueryAttributes as buildCommonQueryAttributes } from '../../../common/utils/quick-find/get-common-query-attributes';
import { canContinueRovoJourney } from '../../../common/utils/quick-find/rovo-journey-id';
import { useSearchDialogAnalytics } from '../../analytics/use-search-dialog-analytics';
import {
	useAnalyticsAttributes,
	useApplicationMode,
	useExperimentConfig,
	usePrimaryProduct,
	useProductEdition,
	useSearchSessionId,
} from '../../store/bootstrap';
import {
	useFetchEntitiesRequestId,
	useFetchSpacesRequestId,
	useQuickFindQuery,
	useRovoJourneyId,
} from '../../store/quick-find';
import {
	useFilterAttributesForTracking,
	useQueryUpdatedTime,
	useQueryVersion,
	useSelectedProduct,
} from '../../store/search';

export const useQuickFindAnalytics = (): ReturnType<typeof useSearchDialogAnalytics> =>
	useSearchDialogAnalytics();

export const getCommonQueryAttributes = (
	query: string,
): ReturnType<typeof buildCommonQueryAttributes> => buildCommonQueryAttributes(query);

export const useQuickFindAttributes: () => {
	commonAttributes: {
		queryUpdatedAgo: number | undefined;
		queryVersion: number;
		searchSessionId: string;
		fetchEntitiesTraceId: string | undefined;
		fetchSpacesTraceId: string | undefined;
		isQuickFind: boolean;
		mode: import('../../../common/constants/application-modes').ApplicationModes;
		productSelected: string;
		filters: string[];
		filterSelections: import('../../../common/utils/events/types').FilterSelectionType[];
		numberOfTerms: number;
		queryHash: string;
		queryHashSHA256: string;
		queryLength: number;
		wordCount: number;
		productEdition: import('@atlassian/search-client').TProductEdition | undefined;
		rovoJourneyId?: string;
		frontendExperiments: {
			activeExperiments: string[];
		};
	};
	nonPrivacySafeAttributes: {
		query: string;
	};
} = () => {
	const [searchSessionId] = useSearchSessionId();
	const [primaryProduct] = usePrimaryProduct();
	const fetchEntitiesRequestId = useFetchEntitiesRequestId();
	const fetchSpacesRequestId = useFetchSpacesRequestId();
	const rovoJourneyId = useRovoJourneyId();
	// eslint-disable-next-line @atlassian/search-common/no-use-rss-hooks
	const [queryUpdatedTime] = useQueryUpdatedTime();
	const quickFindQuery = useQuickFindQuery();
	// eslint-disable-next-line @atlassian/search-common/no-use-rss-hooks
	const [queryVersion] = useQueryVersion();
	const [experimentConfig] = useExperimentConfig();
	const applicationMode = useApplicationMode();
	const [analyticsAttributes] = useAnalyticsAttributes();
	const [productSelected] = useSelectedProduct();
	const filters = useFilterAttributesForTracking();
	const [productEdition] = useProductEdition();

	return useMemo(() => {
		const { frontendExperiments = {} } = experimentConfig;
		const { filterSelections, filters: filterCategories } = filters;

		const commonAttributes = {
			productEdition,
			frontendExperiments: {
				activeExperiments: Object.keys(frontendExperiments).filter(
					(experiment) => frontendExperiments[experiment]?.serveExperiment,
				),
				...frontendExperiments,
			},
			...getCommonQueryAttributes(quickFindQuery),
			queryUpdatedAgo: queryUpdatedTime ? Date.now() - queryUpdatedTime : undefined,
			queryVersion,
			searchSessionId,
			fetchEntitiesTraceId: fetchEntitiesRequestId,
			fetchSpacesTraceId: fetchSpacesRequestId,
			isQuickFind: true,
			mode: applicationMode,
			productSelected: productSelected || ALL_PRODUCTS_STRING,
			filters: filterCategories,
			filterSelections,
			...analyticsAttributes,
			...(canContinueRovoJourney(primaryProduct) && rovoJourneyId && { rovoJourneyId }),
		};

		const nonPrivacySafeAttributes = {
			query: quickFindQuery,
		};

		return { commonAttributes, nonPrivacySafeAttributes };
	}, [
		analyticsAttributes,
		applicationMode,
		experimentConfig,
		fetchEntitiesRequestId,
		fetchSpacesRequestId,
		filters,
		productEdition,
		productSelected,
		primaryProduct,
		queryUpdatedTime,
		queryVersion,
		quickFindQuery,
		rovoJourneyId,
		searchSessionId,
	]);
};
